import { useState, JSX } from 'react';
import { Stack, Group, Text, Badge, Button, Paper } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { Patient, Encounter } from '@medplum/fhirtypes';
import { ConditionListItem, ConditionAction } from './ConditionListItem';
import { ConditionInputModal } from './ConditionInputModal';
import { useConditionActions } from '@/hooks/useConditionActions';
import {
  createNewConditionInputItem,
  ConditionInputItem,
} from '@/utils/condition.utils';

export interface ConditionsListProps {
  conditions: ConditionInputItem[];
  onChange: (conditions: ConditionInputItem[]) => void;
  patient: Patient;
  encounter: Encounter | undefined;
  isMedicalHistory?: boolean;
  readonly?: boolean;
}

export const ConditionsList = ({
  conditions,
  onChange,
  patient,
  encounter,
  isMedicalHistory = false,
  readonly = false,
}: ConditionsListProps): JSX.Element => {
  const { t } = useTranslation(['patient-pages']);

  // Use the condition actions hook for server operations
  const { saveCondition, deleteCondition } = useConditionActions(
    patient,
    encounter
  );

  // Modal states
  const [conditionModalOpened, setConditionModalOpened] = useState(false);
  const [editingCondition, setEditingCondition] = useState<
    ConditionInputItem | undefined
  >();
  const [editingIndex, setEditingIndex] = useState<number | undefined>();
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [deletingIndex, setDeletingIndex] = useState<number | undefined>();

  // Handlers
  const handleAddCondition = () => {
    // Create a new empty condition
    if (!encounter) {
      notifications.show({
        color: 'red',
        title: t('examination.conditions.encounterRequired', {
          defaultValue: 'Encounter is required to add a condition',
        }),
        message: t('examination.conditions.encounterRequiredMessage', {
          defaultValue: 'Please create an encounter first',
        }),
      });
      return;
    }

    const newCondition = createNewConditionInputItem();
    setEditingCondition(newCondition);
    setEditingIndex(undefined);
    setModalMode('add');
    setConditionModalOpened(true);
  };

  const handleAction = async (index: number, action: ConditionAction) => {
    const condition = conditions[index];

    switch (action) {
      case 'edit':
        setEditingCondition(condition);
        setEditingIndex(index);
        setModalMode('edit');
        setConditionModalOpened(true);
        break;

      case 'remove': {
        // If the condition has IDs, it's saved on server - delete it
        if (condition.encounterDiagnosisId || condition.problemListItemId) {
          setDeletingIndex(index);
          try {
            await deleteCondition(condition);
            // Remove from local state after successful server deletion
            const updated = conditions.filter((_, i) => i !== index);
            onChange(updated);
          } catch (error) {
            notifications.show({
              color: 'red',
              title: t('examination.conditions.deleteErrorTitle', {
                defaultValue: 'Failed to remove condition',
              }),
              message: String(error),
            });
          } finally {
            setDeletingIndex(undefined);
          }
        } else {
          // Not saved yet - just remove from local state
          const updated = conditions.filter((_, i) => i !== index);
          onChange(updated);
        }
        break;
      }
    }
  };

  const handleSaveSuccess = (savedCondition: ConditionInputItem) => {
    if (editingIndex !== undefined) {
      // Update existing
      const updated = conditions.map((c, i) =>
        i === editingIndex ? savedCondition : c
      );
      onChange(updated);
    } else {
      // Add new
      onChange([...conditions, savedCondition]);
    }
  };

  return (
    <Stack gap="md">
      {/* Header */}
      <Group gap="xs">
        <Text fw={600} size="md">
          {t('examination.conditions.title', {
            ns: 'patient-pages',
            defaultValue: 'Conditions',
          })}
        </Text>
        {conditions.length > 0 && (
          <Badge size="sm" variant="light">
            {conditions.length}
          </Badge>
        )}
      </Group>

      {/* Empty state */}
      {conditions.length === 0 ? (
        <Paper withBorder radius="md" p="xl" bg="gray.0">
          <Text size="sm" c="dimmed" ta="center">
            {t('summaryPage.summary.conditions.noConditions', {
              ns: 'patient-pages',
              defaultValue: 'No conditions recorded',
            })}
          </Text>
        </Paper>
      ) : (
        <>
          {/* Conditions List */}
          <Stack gap="xs">
            {conditions.map((condition, index) => {
              return (
                <ConditionListItem
                  key={`condition-${index}-${condition.encounterDiagnosisId || condition.problemListItemId || index}`}
                  condition={condition}
                  onAction={(action) => handleAction(index, action)}
                  isDeleting={deletingIndex === index}
                  hideActions={readonly}
                />
              );
            })}
          </Stack>
        </>
      )}

      {/* Add Condition Button */}
      {encounter && !readonly && (
        <Button
          leftSection={<IconPlus size={16} />}
          variant="light"
          size="sm"
          onClick={handleAddCondition}
        >
          {t('examination.conditions.addCondition', {
            ns: 'patient-pages',
            defaultValue: 'Add Condition',
          })}
        </Button>
      )}

      {/* Modal */}
      {encounter && (
        <ConditionInputModal
          opened={conditionModalOpened}
          onClose={() => setConditionModalOpened(false)}
          onSave={saveCondition}
          onSaveSuccess={handleSaveSuccess}
          condition={editingCondition}
          mode={modalMode}
          patient={patient}
          encounter={encounter}
          isMedicalHistory={isMedicalHistory}
        />
      )}
    </Stack>
  );
};
